import { Injectable } from '@angular/core';
import { ConversationGoals } from '@cpd-app/core-module/models';
import { select, Store } from '@ngrx/store';
import { Buffer } from 'buffer';
import { combineLatest, map, withLatestFrom } from 'rxjs';

import { AppState } from '../index';
import { UserProfileFacade } from '../user-profile';
import * as GoalActions from './goal.actions';
import * as GoalSelectors from './goal.selectors';

@Injectable({
  providedIn: 'root'
})
export class GoalFacade {
  public goals$ = this.store.select(GoalSelectors.selectGoals);

  /** GR.6. See `selectGoalsHaveLoaded`. */
  public goalsHaveLoaded$ = this.store.select(
    GoalSelectors.selectGoalsHaveLoaded
  );

  public courses$ = this.store.select(GoalSelectors.selectCourses);

  public certificates$ = this.store.select(GoalSelectors.selectCertificates);

  public institutionProgramGoal$ = this.store.select(
    GoalSelectors.selectInstitutionProgramGoal
  );

  public certification$ = this.store.select(
    GoalSelectors.selectCertificationGoal
  );

  public license$ = this.store.select(GoalSelectors.selectLicenseGoal);

  public isCareerGoalSet$ = this.store.pipe(
    select(GoalSelectors.selectIsCareerGoalSet)
  );

  public isLoadingGoalsReport$ = this.store.pipe(
    select(GoalSelectors.selectLoadingGoalsReport)
  );
  public isAcademicInstitutionGoalSet$ = this.store.pipe(
    select(GoalSelectors.selectIsAcademicInstitutionGoalSet)
  );
  public isAcademicInstitutionProgramGoalSet$ = this.store.pipe(
    select(GoalSelectors.selectIsAcademicInstitutionProgramGoalSet)
  );
  public isCertificationGoalSet$ = this.store.pipe(
    select(GoalSelectors.selectIsCertificationGoalSet)
  );
  public isLicenseGoalSet$ = this.store.pipe(
    select(GoalSelectors.selectIsLicenseGoalSet)
  );

  public isAnyGoalSet$ = this.store.pipe(
    select(GoalSelectors.selectIsAnyGoalSet)
  );
  public areAllGoalsSet$ = this.store.pipe(
    select(GoalSelectors.selectAreAllGoalsSet)
  );
  public isAcademicGoalSet$ = this.store.pipe(
    select(GoalSelectors.selectIsAcademicGoalSet)
  );
  public goalCount$ = this.store.pipe(select(GoalSelectors.selectGoalCount));
  public totalGoalCount$ = this.store.pipe(
    select(GoalSelectors.selectTotalGoalCount)
  );
  public reportDownloaded$ = this.store.pipe(
    select(GoalSelectors.selectReportDownloaded)
  );

  public reportShared$ = this.store.pipe(
    select(GoalSelectors.selectReportShared)
  );

  public aiGoalsReport$ = this.store.pipe(
    select(GoalSelectors.selectAiGoalsReport)
  );

  /** L13. The last report-generation failure, or null. */
  public aiGoalsReportError$ = this.store.pipe(
    select(GoalSelectors.selectAiGoalsReportError)
  );

  /** L13. A refresh request has been sent and not yet answered. */
  public isRefreshingViaInsights$ = this.store.pipe(
    select(GoalSelectors.selectIsRefreshingAiGoalsReport)
  );

  public genViaStatusStream$ = this.store.pipe(
    select(GoalSelectors.selectIsStatusConnected)
  );

  public loadingAiDeepInsight$ = this.store.pipe(
    select(GoalSelectors.selectLoadingAiDeepInsight)
  );

  public reportFile$ = this.store.pipe(select(GoalSelectors.selectReportFile));
  public reportFileSize$ = this.reportFile$.pipe(
    map((file) =>
      file
        ? Math.round(
            Buffer.from(file.split('base64,')[1], 'base64').length /
              Math.pow(1024, 1)
          )
        : 0
    )
  );

  public isEnableToGetReturnLink$ = combineLatest([
    this.userProfileFacade.userProfile$,
    this.userProfileFacade.isAirForce$,
    this.userProfileFacade.isSpaceForce$,
    this.isCareerGoalSet$
  ]).pipe(
    map(([userProfile, isAirForce, isSpaceForce, isCareerGoalSet]) => {
      const defaultCondition =
        !!userProfile &&
        userProfile.isSkillsSurveyComplete &&
        !!isCareerGoalSet;

      if (!isAirForce && !isSpaceForce) {
        return defaultCondition;
      }

      return (
        defaultCondition &&
        userProfile.isLifestyleSurveyComplete &&
        userProfile.isInterestsSurveyComplete &&
        userProfile.isWorkValuesSurveyComplete
      );
    })
  );

  public returnLink$ = this.store.select(GoalSelectors.selectReturnLink).pipe(
    withLatestFrom(this.isEnableToGetReturnLink$),
    map(([returnLink, isEnableToGetReturnLink]) => {
      if (isEnableToGetReturnLink && returnLink) {
        return returnLink;
      }

      this.loadGoalsReportDetails();
      return '';
    })
  );

  public dateGenerated$ = this.store.select(GoalSelectors.selectDateGenerated);

  public readonly genAiGoalsReportSuccessType =
    GoalActions.genAiGoalsReportSuccess.type;

  constructor(
    private store: Store<AppState>,
    private userProfileFacade: UserProfileFacade
  ) {}

  public resetGoalState(): void {
    this.store.dispatch(GoalActions.resetGoalState());
  }

  public loadGoals(): void {
    this.store.dispatch(GoalActions.loadGoals());
  }

  public loadGoalsReportDetails(): void {
    this.store.dispatch(GoalActions.loadGoalsReportDetails());
  }

  public setGoalsFromAiChat(goals: ConversationGoals): void {
    this.store.dispatch(GoalActions.setGoalsFromAiChat({ goals }));
  }

  public refreshViaInsights(): void {
    this.store.dispatch(GoalActions.refreshViaInsight());
  }

  public connectGenStatus(): void {
    this.store.dispatch(GoalActions.connectGenStatus());
  }

  public setCareerGoal(
    onetCode: string,
    occupationTitle: string,
    jobId?: number
  ): void {
    this.store.dispatch(
      GoalActions.setCareerGoal({ onetCode, occupationTitle, jobId })
    );
  }

  public setLearningSuggestions(
    conversationId: number,
    messageId: number
  ): void {
    this.store.dispatch(
      GoalActions.setLearningSuggestionsFromAiChat({
        conversationId,
        messageId
      })
    );
  }

  public setInstitutionGoal(schoolId: number): void {
    this.store.dispatch(GoalActions.setInstitutionGoal({ schoolId }));
  }

  public setCertification(certificationId: string): void {
    this.store.dispatch(GoalActions.setCertificationGoal({ certificationId }));
  }

  public setLicense(licenseId: string): void {
    this.store.dispatch(GoalActions.setLicenseGoal({ licenseId }));
  }

  /**
   * CRED.1. See the action for why the title is passed rather than looked up.
   *
   * CPD-661 item 5: nulls CLEAR the credential goal, and the effect clears the
   * legacy certification alongside it, because one row on the checklist shows
   * either of them.
   */
  public setCredentialGoal(
    credentialCtid: string | null,
    credentialTitle: string | null
  ): void {
    this.store.dispatch(
      GoalActions.setCredentialGoal({ credentialCtid, credentialTitle })
    );
  }

  public setInstitutionProgramGoal(
    schoolId,
    schoolProgramId: number,
    schoolProgramEdLevelId: number
  ): void {
    this.store.dispatch(
      GoalActions.setInstitutionProgramGoal({
        schoolId,
        schoolProgramId,
        schoolProgramEdLevelId
      })
    );
  }

  public clearAllGoals(): void {
    this.store.dispatch(GoalActions.clearAllGoals());
  }

  public setReportDownloaded(reportDownloaded: boolean): void {
    this.store.dispatch(GoalActions.setReportDownloaded({ reportDownloaded }));
  }

  public setReportShared(reportShared: boolean): void {
    this.store.dispatch(GoalActions.setReportShared({ reportShared }));
  }

  public setReportFile(reportFile: string): void {
    this.store.dispatch(GoalActions.setReportFile({ reportFile }));
  }

  public genAiGoalsReport(): void {
    this.store.dispatch(GoalActions.genAiGoalsReport());
  }

  public loadAiDeepInsight(): void {
    this.store.dispatch(GoalActions.loadAiDeepInsight());
  }
}
